import { EXPENSE_CATEGORIES } from '@console/shared-kernel';
import { Banknote } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { components } from '@/lib/api-types-expenses';
import { cn } from '@/lib/utils';

export type ExpenseStatus = components['schemas']['ExpenseStatus'];
export type ManualStatus = components['schemas']['ManualStatus'];
export type DocumentStatus = components['schemas']['DocumentStatus'];
export type Payer = components['schemas']['Payer'];

/** The whole lifecycle, in the order the status picker offers it (spec §3.3). */
export const EXPENSE_STATUSES: readonly ExpenseStatus[] = [
  'draft',
  'awaiting_document',
  'submitted',
  'more_info_requested',
  'accepted',
  'refused',
  'repayment_requested',
  'repaid',
  'reimbursed',
];

/**
 * What «Cambia stato» may set by hand (spec §3.3, platform's review of #515): never `draft` or
 * `submitted`, which belong to the owner's own flow. The server refuses anything else.
 */
export const MANUAL_STATUSES: readonly ManualStatus[] = EXPENSE_STATUSES.filter(
  (status): status is ManualStatus => status !== 'draft' && status !== 'submitted',
);

export const DOCUMENT_STATUSES: readonly DocumentStatus[] = ['attached', 'awaited', 'not-required'];

export const PAYERS: readonly Payer[] = [
  'company-card',
  'company-transfer',
  'personal',
  'director-advance',
];

/**
 * A category's label in the reader's language, from the shared constant (#501). An unknown code
 * reads as it came, so a category added on the server never blanks a row.
 */
export function categoryLabel(code: string, language: string | undefined): string {
  const category = EXPENSE_CATEGORIES.find((candidate) => candidate.code === code);
  if (category === undefined) return code;
  return language?.startsWith('en') ? category.labelEn : category.labelIt;
}

/** One date format, dd/mm/yyyy (U17): the short and the long names are the same helper. */
export { formatDate as formatShortDate, formatDate as formatLongDate } from '../format-date';

const STATUS_TONE: Record<ExpenseStatus, Tone> = {
  draft: 'neutral',
  awaiting_document: 'warning',
  submitted: 'neutral',
  more_info_requested: 'warning',
  accepted: 'success',
  refused: 'error',
  repayment_requested: 'warning',
  repaid: 'neutral',
  reimbursed: 'success',
};

const DOCUMENT_TONE: Record<DocumentStatus, Tone> = {
  attached: 'success',
  awaited: 'warning',
  'not-required': 'neutral',
};

export type Tone = 'neutral' | 'info' | 'success' | 'warning' | 'error';

const TONE_CLASS: Record<Tone, string> = {
  neutral: 'bg-muted text-muted-foreground',
  info: 'bg-[var(--info-tint)] text-[var(--info)]',
  success: 'bg-success-tint text-success',
  warning: 'bg-warning-tint text-warning',
  error: 'bg-error-tint text-destructive',
};

export function Badge({ tone, children }: { readonly tone: Tone; readonly children: ReactNode }) {
  return (
    <span
      data-tone={tone}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[12.5px] font-semibold whitespace-nowrap',
        "before:size-1.5 before:rounded-full before:bg-current before:content-['']",
        TONE_CLASS[tone],
      )}
    >
      {children}
    </span>
  );
}

/** «Bozza», «Accettata»…: the expense's state, coloured by what it asks of the reader. */
export function StatusBadge({ status }: { readonly status: ExpenseStatus }) {
  const { t } = useTranslation('expenses');
  return <Badge tone={STATUS_TONE[status]}>{t(`list.status.${status}`)}</Badge>;
}

/**
 * The document badge (the board's labels): Allegato · Manca · Non richiesto. `missing` wins over
 * the stored status: an `attached` row with nothing stored yet still reads «Manca».
 */
export function DocumentBadge({
  status,
  missing,
}: {
  readonly status: DocumentStatus;
  readonly missing?: boolean | undefined;
}) {
  const { t } = useTranslation('expenses');
  const shown: DocumentStatus = missing === true ? 'awaited' : status;
  return <Badge tone={DOCUMENT_TONE[shown]}>{t(`list.document.${shown}`)}</Badge>;
}

/** «Non tracciabile»: a cash expense, recorded but not reimbursed (decision 145). */
export function UntraceableBadge({ traceable }: { readonly traceable: boolean }) {
  const { t } = useTranslation('expenses');
  return traceable ? null : (
    <Badge tone="warning">
      <Banknote size={12} aria-hidden />
      {t('list.badges.untraceable')}
    </Badge>
  );
}

/** «Non rimborsabile»: traceable but nothing to give back (the company paid). */
export function NotReimbursableBadge({
  traceable,
  reimbursable,
}: {
  readonly traceable: boolean;
  readonly reimbursable: boolean;
}) {
  const { t } = useTranslation('expenses');
  return traceable && !reimbursable ? (
    <Badge tone="neutral">{t('list.badges.notReimbursable')}</Badge>
  ) : null;
}
